import { api } from "@/api/client";

/**
 * Notes Module v1.0 §6 — typed wire shapes for the central Notes API.
 *
 * The legacy `notesApi` from `@/api/ux` (now routed at `/api/internal-notes`)
 * still backs the cross-entity Activity Timeline and the Lead detail page's
 * inline notes block; this client owns the new `<NotesTabPanel>` surface.
 */
export type NoteActivityType =
  | "Posted"
  | "Edited"
  | "AuthorDeleted"
  | "ComplianceDeleted";

export interface NoteDto {
  id: string;
  ownerType: string;
  ownerId: string;
  authorId: string;
  authorDisplayName: string | null;
  authorRoleCode: string | null;
  body: string;
  createdAt: string;
  modifiedAt: string | null;
  /** Server-computed: author + within edit window. */
  canEdit: boolean;
  /** Server-computed: (author + window) OR Compliance. */
  canDelete: boolean;
  /** Server-computed: caller holds Notes.Moderate. */
  canModerate: boolean;
}

export interface PostNotePayload {
  ownerType: string;
  ownerId: string;
  body: string;
}

export const notesV1Api = {
  post: (payload: PostNotePayload) => api.post<NoteDto>("notes", payload),

  listByOwner: (ownerType: string, ownerId: string, skip = 0, take = 50) =>
    api.get<NoteDto[]>(
      `notes/by-owner/${encodeURIComponent(ownerType)}/${encodeURIComponent(ownerId)}`,
      { skip, take },
    ),

  recentCount: (ownerType: string, ownerId: string, windowDays = 7) =>
    api.get<number>(
      `notes/by-owner/${encodeURIComponent(ownerType)}/${encodeURIComponent(ownerId)}/recent-count`,
      { windowDays },
    ),

  edit: (id: string, body: string) =>
    api.patch<NoteDto>(`notes/${id}`, { body }),

  remove: (id: string) => api.delete<void>(`notes/${id}`),

  moderateRemove: (id: string, reason: string) =>
    api.delete<void>(`notes/${id}/moderate`, { reason }),
};
